import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { COLOR_MODE_STORAGE_KEY, DARK_SCHEME_QUERY, isDarkModeEnabled, resolveColorMode, type ColorMode } from "./colorModeScript";
export { COLOR_MODE_STORAGE_KEY };
export type { ColorMode };
interface ColorModeContextType {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}
export const ColorModeContext = createContext<ColorModeContextType | undefined>(undefined);
export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
};
const readStored = (): string | null => {
  try {
    return localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  } catch {
    return null;
  }
};
const apply = (mode: ColorMode, remember: boolean) => {
  document.documentElement.dataset.mode = mode;
  if (!remember) return;
  try {
    localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
  } catch {
    // Private browsing; the mode still applies for this session.
  }
};
export const ColorModeProvider: React.FC<{
  children: ReactNode;
}> = ({
  children
}) => {
  /**
   * Seeded from the DOM, because the boot script in _document.tsx has already
   * resolved stored choice against the OS before paint. Re-deriving it here
   * would risk disagreeing with what the user is already looking at.
   */
  const [colorMode, setMode] = useState<ColorMode>(() => {
    if (!isDarkModeEnabled()) return "light";
    if (typeof document === "undefined") return "light";
    return document.documentElement.dataset.mode as ColorMode ?? "light";
  });
  const setColorMode = useCallback((mode: ColorMode) => {
    setMode(mode);
    apply(mode, true);
  }, []);
  const toggleColorMode = useCallback(() => {
    setMode(previous => {
      const next = previous === "dark" ? "light" : "dark";
      apply(next, true);
      return next;
    });
  }, []);

  // Keep other tabs in step; `storage` only fires in tabs that did not write.
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key !== COLOR_MODE_STORAGE_KEY) return;
      if (event.newValue !== "light" && event.newValue !== "dark") return;
      setMode(event.newValue);
      apply(event.newValue, false);
    };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  /**
   * Follow the OS as it changes, but only while the visitor has made no choice
   * of their own — once they pick a mode it outranks the OS until they clear it.
   */
  useEffect(() => {
    if (!isDarkModeEnabled()) return;
    const query = window.matchMedia(DARK_SCHEME_QUERY);
    const follow = () => {
      if (readStored()) return;
      const next = resolveColorMode(null, query.matches);
      setMode(next);
      apply(next, false);
    };
    query.addEventListener("change", follow);
    return () => query.removeEventListener("change", follow);
  }, []);
  return <ColorModeContext.Provider value={{
    colorMode,
    setColorMode,
    toggleColorMode
  }} data-sentry-element="unknown" data-sentry-component="ColorModeProvider" data-sentry-source-file="ColorModeContext.tsx">
      {children}
    </ColorModeContext.Provider>;
};